import {
  REHAB_FORM_NAMES,
  REHAB_FORM_SUB_TYPES,
  REHAB_FORM_TYPES,
  TRACK_EVENTS,
} from "core/consts";
import { getGKVGeneralFormSchema } from "core/schemas/GKVGeneralFormSchema";
import { getGKVMedicalFormSchema } from "core/schemas/GKVMedicalFormSchema";
import {
  extractInvalidFields,
  flattenZodFieldErrorsDeep,
  unflattenErrors,
} from "core/schemas/utils";
import {
  Auction,
  GetOntologyType,
  GKVGeneralFormData,
  GKVMedicalFormData,
  RehabFormName,
  RehabFormSubType,
  RehabFormType,
  SchemaVersion,
  TrackEventFn,
  ValueOf,
} from "core/types";
import { PDFForm } from "pdf-lib";
import React from "react";
import { ConversionJobs, ValidationFunction } from "react-forms-state";
import { ObservableValue } from "react-forms-state/src/Context";
import { Observable } from "rxjs";
import Translations from "translations/types";
import { DRVGeneralFormPresenter } from "./DRVGeneralForm/FormSections";
import { importAuctionToDRVGeneralForm } from "./DRVGeneralForm/importAuction";
import { DRVgeneralFormDefinition } from "./DRVGeneralForm/modelDefinition";
import { DRVGeneralFormMapping } from "./DRVGeneralForm/pdfMapping";
import { DRVMedicalFormPresenter } from "./DRVMedicalForm/FormSections";
import { importAuctionToDRVMedicalForm } from "./DRVMedicalForm/importAuction";
import { DRVmedicalFormDefinition } from "./DRVMedicalForm/modelDefinition";
import { DRVMedicalFormMapping } from "./DRVMedicalForm/pdfMapping";
import { GKVGeneralFormPresenter } from "./GKVGeneralForm";
import { importAuctionToGKVGeneralForm } from "./GKVGeneralForm/importAuction";
import { GKVgeneralFormDefinition } from "./GKVGeneralForm/modelDefinition";
import { GKVMedicalFormPresenter } from "./GKVMedicalForm";
import { importAuctionToGKVMedicalForm } from "./GKVMedicalForm/importAuction";
import { GKVmedicalFormDefinition } from "./GKVMedicalForm/modelDefinition";
import { RehabFormDataMapped } from "./utils";

export const GKV_GENERAL_FORM_PATH =
  "rehab_general_universal_third_draft.pdf" as const;
export const GKV_MEDICAL_FORM_PATH =
  "rehab_medical_universal_fifth_draft.pdf" as const;
export const DRV_GENERAL_FORM_PATH = "general_pension_form_v3.pdf" as const;
export const DRV_MEDICAL_FORM_PATH = "medical_pension_form_v1.1.pdf" as const;

export const GKV_GENERAL_FILE_NAME =
  "antrag_anschlussrehabilitation.pdf" as const;
export const GKV_MEDICAL_FILE_NAME =
  "antrag_aerztlicher_befundbericht.pdf" as const;
export const DRV_GENERAL_FORM_NAME = "drv_antrag_G0250.pdf" as const;
export const DRV_MEDICAL_FORM_NAME = "drv_G0260_G0600.pdf" as const;

const _REHAB_FORM_PATHS = [
  GKV_GENERAL_FORM_PATH,
  GKV_MEDICAL_FORM_PATH,
  DRV_GENERAL_FORM_PATH,
  DRV_MEDICAL_FORM_PATH,
] as const;

export type RehabFormPath = (typeof _REHAB_FORM_PATHS)[number];

const _REHAB_FORM_FILE_NAMES = [
  GKV_GENERAL_FILE_NAME,
  GKV_MEDICAL_FILE_NAME,
  DRV_GENERAL_FORM_NAME,
  DRV_MEDICAL_FORM_NAME,
] as const;

export type RehabFormFileName = (typeof _REHAB_FORM_FILE_NAMES)[number];

export type RehabFormFormGetters<T extends RehabFormName> = Partial<
  Record<
    keyof RehabFormDataMapped<T>,
    (auction: Auction) => ValueOf<RehabFormDataMapped<T>> | null
  >
>;

export type PdfFillerProps<T extends RehabFormName> = {
  form: PDFForm;
  formData: RehabFormDataMapped<T>;
  locale: Locale;
};

export type PdfFiller<T extends RehabFormName> = ({
  form,
  formData,
  locale,
}: PdfFillerProps<T>) => void;

type FormConfig = {
  Form: (props: {
    auction?: Auction;
    onChange: (value: any, statePath: string, validation?: any) => void;
    schemaVersion?: SchemaVersion;
    shouldSkipRequiredValidation: boolean;
    valueChangeObs: Observable<ObservableValue> | undefined;
  }) => React.JSX.Element;
  convertOut?: (value: any, props: any) => AnyObject;
  getFormDescription: (translations: Translations) => string;
  getFormTitle: (translations: Translations) => string;
  importAuctionToFormGetters: (
    getOntology: GetOntologyType,
    translations: Translations,
  ) => RehabFormFormGetters<any>;
  modelDefinition: ConversionJobs;
  validate?: ValidationFunction;
};

type PdfConfig = {
  blankPdfAssetPath: RehabFormPath;
  downloadPdfFileName: RehabFormFileName;
  pdfFiller?: PdfFiller<any>;
};

type RehabFormConfig = {
  formConfig: FormConfig;
  formSubType: RehabFormSubType;
  formType: RehabFormType;
  pdfConfig: PdfConfig;
};

export const REHAB_FORM_CONFIGS: Record<RehabFormName, RehabFormConfig> = {
  [REHAB_FORM_NAMES.DRV_GENERAL]: {
    formConfig: {
      Form: DRVGeneralFormPresenter,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.drvGeneralForm.description,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.drvGeneralForm.title,
      importAuctionToFormGetters: importAuctionToDRVGeneralForm,
      modelDefinition: DRVgeneralFormDefinition,
    },
    formSubType: REHAB_FORM_SUB_TYPES.DRV,
    formType: REHAB_FORM_TYPES.GENERAL,
    pdfConfig: {
      blankPdfAssetPath: DRV_GENERAL_FORM_PATH,
      downloadPdfFileName: DRV_GENERAL_FORM_NAME,
      pdfFiller: DRVGeneralFormMapping,
    },
  },
  [REHAB_FORM_NAMES.DRV_MEDICAL]: {
    formConfig: {
      Form: DRVMedicalFormPresenter,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.drvMedicalForm.description,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.drvMedicalForm.title,
      importAuctionToFormGetters: importAuctionToDRVMedicalForm,
      modelDefinition: DRVmedicalFormDefinition,
    },
    formSubType: REHAB_FORM_SUB_TYPES.DRV,
    formType: REHAB_FORM_TYPES.MEDICAL,
    pdfConfig: {
      blankPdfAssetPath: DRV_MEDICAL_FORM_PATH,
      downloadPdfFileName: DRV_MEDICAL_FORM_NAME,
      pdfFiller: DRVMedicalFormMapping,
    },
  },
  [REHAB_FORM_NAMES.GKV_GENERAL]: {
    formConfig: {
      Form: GKVGeneralFormPresenter,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.generalForm.description,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.generalForm.title,
      importAuctionToFormGetters: importAuctionToGKVGeneralForm,
      modelDefinition: GKVgeneralFormDefinition,
      validate(
        value: GKVGeneralFormData,
        props: {
          schemaVersion?: SchemaVersion;
          skipValidation?: boolean;
          trackEvent?: TrackEventFn;
          translations: Translations;
        },
      ) {
        const { schemaVersion, skipValidation, trackEvent, translations } =
          props;

        if (skipValidation) {
          return true;
        }

        const result = getGKVGeneralFormSchema({
          isRedesignActive: false,
          schemaVersion,
          translations,
        }).safeParse(value);
        if (!result.success) {
          const flattened = flattenZodFieldErrorsDeep(result.error);
          const nested = unflattenErrors(flattened);

          if (trackEvent) {
            const invalidFields = extractInvalidFields(result.error.issues);

            trackEvent({
              formName: REHAB_FORM_NAMES.GKV_GENERAL,
              invalidFields,
              name: TRACK_EVENTS.AHB_FORM_VALIDATION_FAILED,
              totalIssues: result.error.issues.length,
            });
          }

          return nested;
        }
        return true;
      },
    },
    formSubType: REHAB_FORM_SUB_TYPES.GKV,
    formType: REHAB_FORM_TYPES.GENERAL,
    pdfConfig: {
      blankPdfAssetPath: GKV_GENERAL_FORM_PATH,
      downloadPdfFileName: GKV_GENERAL_FILE_NAME,
      // pdfFiller: GKVGeneralFormMapping, #TODO will have to be re-implemented later
    },
  },
  [REHAB_FORM_NAMES.GKV_MEDICAL]: {
    formConfig: {
      Form: GKVMedicalFormPresenter,
      getFormDescription: (translations: Translations) =>
        translations.patientForms.medicalForm.description,
      getFormTitle: (translations: Translations) =>
        translations.patientForms.medicalForm.title,
      importAuctionToFormGetters: importAuctionToGKVMedicalForm,
      modelDefinition: GKVmedicalFormDefinition,
      validate(
        value: GKVMedicalFormData,
        props: {
          skipRequiredValidation?: boolean;
          skipValidation?: boolean;
          trackEvent?: TrackEventFn;
          translations: Translations;
        },
      ) {
        const {
          skipRequiredValidation,
          skipValidation,
          trackEvent,
          translations,
        } = props;

        if (skipValidation) {
          return true;
        }

        const result = getGKVMedicalFormSchema({
          skipRequiredValidation,
          translations,
        }).safeParse(value);

        if (!result.success) {
          const flattened = flattenZodFieldErrorsDeep(result.error);
          const nested = unflattenErrors(flattened);

          if (trackEvent) {
            const invalidFields = extractInvalidFields(result.error.issues);

            trackEvent({
              formName: REHAB_FORM_NAMES.GKV_MEDICAL,
              invalidFields,
              name: TRACK_EVENTS.AHB_FORM_VALIDATION_FAILED,
              totalIssues: result.error.issues.length,
            });
          }

          return nested;
        }
        return true;
      },
    },
    formSubType: REHAB_FORM_SUB_TYPES.GKV,
    formType: REHAB_FORM_TYPES.MEDICAL,
    pdfConfig: {
      blankPdfAssetPath: GKV_MEDICAL_FORM_PATH,
      downloadPdfFileName: GKV_MEDICAL_FILE_NAME,
      // pdfFiller: GKVmedicalFormMapping, #TODO will have to be re-implemented later
    },
  },
};

export const getRehabFormConfig = (formName: RehabFormName): RehabFormConfig =>
  REHAB_FORM_CONFIGS[formName];
